import { PlusIcon, PrinterIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setLoanData } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { PlainInput } from 'components/PlainInput'
import { SaveChanges } from 'components/SaveChanges'
import {
  customOptionLabels,
  editableOptionFields,
  FieldOrigin,
  getInitialLoanFields,
  INVALID_ALL_INPUTS,
  IVisibleProp,
  loanCustomRangeOptionFields,
  loanCustomSelectOptionFields,
} from 'config'
import { convertSelectRangeOptionsToMap } from 'pages/Admin/AdminTools/Configuration/LoanFieldsProperties/utils'
import {
  IUwSummaryConfig,
  UwSummarySection,
  UwSummarySectionText,
} from 'pages/Admin/AdminTools/Configuration/UnderwritingSummary/types'
import { loanOverviewAndStepLogic } from 'pages/LoanOverview/loanOverviewAndStepLogic'
import type { ILoan, ILoanDetail } from 'pages/LoanStructure/interfaces'
import { visibleLoansLogic } from 'pages/LoanStructure/Logic'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { store } from 'reducers'
import {
  downloadFile,
  downloadUnderwritingSummaryPdf,
  getAdminConfig,
  getGeoLocation,
  getUnderwritingSummary,
  updateUnderwritingSummary,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Button, Checkbox } from 'stories/components'
import { formatDateYMD, InputConvert, InputValidate, openAuditLog, removeComma } from 'utils'
import { isVisibleFieldVisibleProps } from 'utils/loan'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import {
  hasNAFields,
  recordingInfoFields,
  RecordingInformationOptions,
  sourceOfBorrowersInterestOptions,
  uwInputGroups,
  uwSummaryDefaultInputs,
} from './constants'

export default function UnderwritingSummary({ child = false, title = 'Underwriting Summary' }) {
  const { loan, loanDetail, fieldProperties } = useSelector((state: any) => {
    return {
      loan: state.loan as ILoan,
      loanDetail: state.loanDetail as ILoanDetail,
      fieldProperties: ((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})[FieldOrigin.LoanStructure] ||
        {}) as Record<string, IVisibleProp>,
    }
  })

  const navigate = useHistory()
  const [inputs, setInputs] = useState(uwSummaryDefaultInputs())
  const [data, setData] = useState<Record<string, any>>({})
  const loanInputs = getInitialLoanFields(false, !!loanDetail.loanGlobalSettings?.allowCentForLoanAmount)
  const [isLoading, setLoading] = useState(false)
  const [init, setInit] = useState(true)
  const [changed, setChanged] = useState(false)
  const [recordingInfo, setRecordingInfo] = useState({
    book: '',
    page: '',
    instrument: '',
  })
  const [recordingInfoError, setRecordingInfoError] = useState({
    book: '',
    page: '',
    instrument: '',
  })
  const [latLong, setLatLong] = useState({ lat: 0, lng: 0 })
  const [config, setConfig] = useState<IUwSummaryConfig>({
    [UwSummarySection.UnderwritingInformation]: {},
    [UwSummarySection.PropertyConstruction]: {},
    [UwSummarySection.BorrowerCreditInfo]: {},
    [UwSummarySection.BorrowerInterestSource]: {},
    [UwSummarySection.SeniorLienInformation]: {},
    [UwSummarySection.Exception]: {},
  })

  const [exceptionConfig, setExceptionConfig] = useState<Record<string, any>>({
    types: [],
    factors: [],
    submissionMessage: '',
    filesRequired: false,
  })

  const fieldIsVisible = (section: UwSummarySection, key: string) => {
    const field = config?.[section]?.[key]
    if (!field) return true
    if (field.visible === false) return false
    if (field.visibleLogic?.length && !isVisibleFieldVisibleProps({ visibleLogic: field.visibleLogic }, loan)) return false
    return true
  }

  const dispatch = useDispatch()

  useEffect(() => {
    initLoad()
  }, [])

  const initLoad = async () => {
    const nInputs = cloneDeep(inputs)
    const newRecordingInfo = cloneDeep(recordingInfo)

    if (loan.transactionType === 'Purchase') {
      delete nInputs.BorrowersInterestSourceTypeInSubjectProperty
      delete nInputs.BorrowersInterestSourceTitleInSubjectProperty
      delete nInputs.RecordingInfo
      delete nInputs.BorrowersInterestSourceDateInSubjectProperty
      nInputs.purchasePrice.disabled = true
    } else {
      nInputs.purchasePrice.disabled = false
    }

    setLoading(true)

    const loanNumber = setLoanNumber()
    if (loan.no != loanNumber) {
      const loaded = await loanOverviewAndStepLogic(loanNumber, '')
      if (loaded.stale) {
        setLoading(false)
        return
      }
      if (!loaded.success) {
        return navigate.push('/pipeline')
      }
    }

    const [res, exceptionConfig] = await Promise.all([getAdminConfig('uwConfig'), getAdminConfig('exceptionRequest')])
    setExceptionConfig(exceptionConfig || {})
    setConfig(res || {})

    getUnderwritingSummary()
      .then((data) => {
        setData(data)
        Object.keys(nInputs).forEach((key) => {
          if (key === 'RecordingInfo') {
            if (Object.keys(RecordingInformationOptions).includes(data[key])) nInputs[key].value = data[key]
            else nInputs[key].value = ''
          } else if (key === 'DisclosedLitigation') {
            const saved = data[key]
            nInputs[key].value = Array.isArray(saved) ? saved : saved ? [String(saved)] : []
          } else nInputs[key].value = data[key]
          let _config: Record<string, any> = {}
          Object.keys(res).map((key) => (_config = { ..._config, ...res[key] }))
          nInputs[key].required = !!_config[key]?.required
          nInputs[key].error = InputValidate(nInputs[key])
        })
        ;['book', 'page', 'instrument'].forEach((key) => {
          if (!!data[key]) (newRecordingInfo as any)[key] = data[key]
          else (newRecordingInfo as any)[key] = ''
        })

        const propertyType = store.getState().loan?.propertyType
        if (propertyType !== 'Manufactured') {
          nInputs.ManufacturedHomeLandPropertyInterest.value = '5'
        }

        setInputs(nInputs)
        setRecordingInfo(newRecordingInfo)
        setLoading(false)
        setInit(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    const geoLocation = loan.geoLocation
    if (geoLocation && geoLocation.lat && geoLocation.lng) {
      setLatLong(geoLocation)
    } else {
      getGeoLocation().then((res) => {
        dispatch(setLoanData({ key: 'geoLocation', data: res }))
        setLatLong(res)
      })
    }
  }, [])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].value = value

    let input = newInputs[key]
    if (key == 'Mers' && !!value) input = { ...newInputs[key], required: true }

    newInputs[key].error = InputValidate(input)

    if (key === 'BorrowersInterestSourceTypeInSubjectProperty') {
      if (value === 'other') newInputs['BorrowersInterestSourceTitleInSubjectProperty'].value = ''
      else newInputs['BorrowersInterestSourceTitleInSubjectProperty'].value = sourceOfBorrowersInterestOptions[value]
    }

    setChanged(true)
    setInputs(newInputs)
  }

  const updateLitigationLines = (lines: string[]) => {
    const newInputs = cloneDeep(inputs)
    newInputs.DisclosedLitigation.value = lines
    newInputs.DisclosedLitigation.error = ''
    setChanged(true)
    setInputs(newInputs)
  }

  const validateRecordingInfo = () => {
    const newRecordingInfoError = cloneDeep(recordingInfoError)

    let hasError = false

    if (recordingInfoFields[inputs.RecordingInfo.value])
      recordingInfoFields[inputs.RecordingInfo.value].forEach((key) => {
        if (!(recordingInfo as any)[key]) {
          hasError = false
          ;(newRecordingInfoError as any)[key] = ''
        } else {
          ;(newRecordingInfoError as any)[key] = ''
        }
      })

    setRecordingInfoError(newRecordingInfoError)
    return hasError
  }

  const onSave = () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)
    let saveData: Record<string, any> = {}

    Object.keys(newInputs).forEach((key) => {
      const { value } = newInputs[key]
      let input = newInputs[key]
      if (key == 'Mers' && !!value) input = { ...newInputs[key], required: true }

      newInputs[key].error = InputValidate(input)
      saveData[key] = value
      if (newInputs[key].error) hasError = true
    })

    hasNAFields.map((key) => {
      saveData[`${key}NA`] = !!data[`${key}NA`] as any
    })

    setChanged(false)
    setInputs(newInputs)

    if (loan.transactionType === 'Refinance') {
      if (inputs.RecordingInfo.value)
        recordingInfoFields[inputs.RecordingInfo.value].forEach((key) => {
          saveData[key] = (recordingInfo as any)[key]
        })

      hasError = hasError || validateRecordingInfo()
    }

    if (hasError) {
      return toast(INVALID_ALL_INPUTS, { type: 'error' })
    }

    if (loan.propertyType !== 'Manufactured') {
      saveData.ManufacturedHomeLandPropertyInterest = '5'
    }

    setLoading(true)

    updateUnderwritingSummary(saveData)
      .then(() => {
        toast('Successfully updated', { type: 'info' })
        if (saveData.InterestType !== undefined) {
          dispatch(setLoanData({ key: 'interestType', data: saveData.InterestType }))
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  const showHistory = (key: string) => {
    const options = {
      table: 'Underwriting',
      field: key,
      keys: {
        field: key,
      },
    }
    openAuditLog(options)
  }

  const onDownload = async () => {
    const loanNumber = setLoanNumber()
    setLoading(true)

    const pdfGroups = cloneDeep(uwInputGroups[loan.transactionType])
    Object.keys(pdfGroups).forEach((title) => {
      const sectionKey = Object.values(UwSummarySection).find((value) => UwSummarySectionText[value] === title)
      pdfGroups[title] = pdfGroups[title].filter((key) =>
        fieldIsVisible(sectionKey || UwSummarySection.UnderwritingInformation, key),
      )
    })

    const pdfData = await downloadUnderwritingSummaryPdf(pdfGroups, inputs, visibleLoanInputs, title)
    downloadFile(`${title} ${loanNumber}(${formatDateYMD()}).pdf`, pdfData)
    setLoading(false)
  }

  const onChangeRecordingInfo = (key: 'book' | 'page' | 'instrument', value: string) => {
    const newRecordingInfo = cloneDeep(recordingInfo)

    newRecordingInfo[key] = value
    setRecordingInfo(newRecordingInfo)
  }

  const onChangeNA = async (key: string, value: any) => {
    const temp = cloneDeep(data)
    temp[key] = value
    switch (key) {
      case 'AsIsValueNA':
        temp['AsIsAppraisedValueNA'] = !value
        break
      case 'AsIsAppraisedValueNA':
        temp['AsIsValueNA'] = !value
        break
      case 'AfterRepairValueNA':
        temp['AfterRepairAppraisedValueNA'] = !value
        break
      case 'AfterRepairAppraisedValueNA':
        temp['AfterRepairValueNA'] = !value
        break
      case 'MarketRentNA':
        temp['MonthlyRentNA'] = !value
        break
      case 'MonthlyRentNA':
        temp['MarketRentNA'] = !value
        break
    }
    setData(temp)
  }

  const renderDeedRecordingDate = () => {
    if (loan.transactionType === 'Purchase' || !inputs.DeedRecordingDate) return null
    if (!fieldIsVisible(UwSummarySection.BorrowerInterestSource, 'DeedRecordingDate')) return null
    const input = cloneDeep(inputs.DeedRecordingDate)
    input.required = config[UwSummarySection.BorrowerInterestSource]?.DeedRecordingDate?.required === true
    input.history = !input.disabled
    return (
      <div className="w-72 ml-auto">
        <RenderInput input={input} Key="DeedRecordingDate" onChange={onChange} showHistory={showHistory} />
      </div>
    )
  }

  const renderRecordingInformation = () => {
    if (loan.transactionType === 'Purchase') return <></>

    let controls: JSX.Element | null = null
    switch (inputs.RecordingInfo?.value) {
      case 'bookAndPageOnly':
        controls = (
          <div className="flex items-center gap-2">
            <span>Book</span>
            <div className={`border-b w-40 ${recordingInfoError.book && 'border-rose-700'}`}>
              <PlainInput
                value={recordingInfo.book}
                content={recordingInfo.book}
                type="text"
                onChange={(value: any) => onChangeRecordingInfo('book', value)}
              />
            </div>
            <span>and</span>
            <span>Page</span>
            <div className={`border-b w-16 ${recordingInfoError.page && 'border-rose-700'}`}>
              <PlainInput
                value={recordingInfo.page}
                content={recordingInfo.page}
                type="text"
                onChange={(value: any) => onChangeRecordingInfo('page', value)}
              />
            </div>
          </div>
        )
        break
      case 'instrumentOnly':
        controls = (
          <div className="flex items-center gap-2">
            <span>Instrument #</span>
            <div className={`border-b w-60 ${recordingInfoError.instrument && 'border-rose-700'}`}>
              <PlainInput
                value={recordingInfo.instrument}
                content={recordingInfo.instrument}
                type="text"
                onChange={(value: any) => onChangeRecordingInfo('instrument', value)}
              />
            </div>
          </div>
        )
        break
      case 'bookAndPageWithInstrument':
        controls = (
          <div className="flex items-center flex-wrap gap-2">
            <span>Book</span>
            <div className={`border-b w-40 ${recordingInfoError.book && 'border-rose-700'}`}>
              <PlainInput
                value={recordingInfo.book}
                content={recordingInfo.book}
                type="text"
                onChange={(value: any) => onChangeRecordingInfo('book', value)}
              />
            </div>
            <span>and</span>
            <span>Page</span>
            <div className={`border-b w-16 ${recordingInfoError.page && 'border-rose-700'}`}>
              <PlainInput
                value={recordingInfo.page}
                content={recordingInfo.page}
                type="text"
                onChange={(value: any) => onChangeRecordingInfo('page', value)}
              />
            </div>
            <span>under</span>
            <span>Instrument #</span>
            <div className={`border-b w-60 ${recordingInfoError.instrument && 'border-rose-700'}`}>
              <PlainInput
                value={recordingInfo.instrument}
                content={recordingInfo.instrument}
                type="text"
                onChange={(value: any) => onChangeRecordingInfo('instrument', value)}
              />
            </div>
          </div>
        )
        break
    }

    const deedDate = renderDeedRecordingDate()
    if (!controls && !deedDate) return <></>
    return (
      <div className="flex items-end gap-4 flex-wrap w-full">
        {controls}
        {deedDate}
      </div>
    )
  }

  const renderDisclosedLitigation = () => {
    const lines: string[] = Array.isArray(inputs.DisclosedLitigation?.value) ? inputs.DisclosedLitigation.value : []
    return (
      <div className="input md:col-span-4" key="DisclosedLitigation">
        <div className="border-b border-shade-blue italic flex items-center gap-3 mb-2">
          <span>Disclosed Litigation</span>
          <button
            type="button"
            className="not-italic text-shade-blue inline-flex items-center gap-1 text-sm"
            onClick={() => updateLitigationLines([...lines, ''])}
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {lines.map((line, index) => (
            <div className="flex items-center gap-2" key={index}>
              <div className="border-b flex-1">
                <PlainInput
                  value={line}
                  content={line}
                  type="text"
                  onChange={(value: string) => {
                    const next = [...lines]
                    next[index] = value
                    updateLitigationLines(next)
                  }}
                />
              </div>
              <button
                type="button"
                className="text-red-600"
                aria-label={`Remove litigation line ${index + 1}`}
                onClick={() => updateLitigationLines(lines.filter((_, lineIndex) => lineIndex !== index))}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const visibleLoanInputs = useMemo(() => {
    const visibleInputs = visibleLoansLogic()

    const rtl: Record<string, any> = {}
    visibleInputs.map((key) => {
      if (key == 'subjectPropertyMap') return null
      if (!fieldIsVisible(UwSummarySection.UnderwritingInformation, key)) return null

      const input = { ...loanInputs[key], disabled: true }
      input.value = (loan as any)[key]

      input.title = fieldProperties[key] && fieldProperties[key].title ? fieldProperties[key].title || '' : input.title

      input.inputType = (
        fieldProperties[key] && fieldProperties[key].inputType ? fieldProperties[key].inputType : input.inputType
      ) as any
      input.type = (fieldProperties[key] && fieldProperties[key].type ? fieldProperties[key].type : input.type) as any

      if (
        fieldProperties[key] &&
        !!fieldProperties[key]?.options &&
        !!fieldProperties[key].options?.length &&
        [...loanCustomRangeOptionFields, ...loanCustomSelectOptionFields].includes(key)
      ) {
        ;(input as any).options = convertSelectRangeOptionsToMap(fieldProperties[key]?.options || [])
      }
      if (fieldProperties[key] && editableOptionFields.includes(key) && fieldProperties[key]?.options?.length) {
        const labels = customOptionLabels(fieldProperties[key].options as any)
        if (labels) (input as any).options = labels
      } else if (fieldProperties[key]?.options && !(input as any).options)
        (input as any).options = fieldProperties[key]?.options

      rtl[key] = input
    })
    return rtl
  }, [loanInputs, loan, fieldProperties, config])

  const renderLoanInputs = () => {
    return (
      <>
        {Object.keys(visibleLoanInputs).map((key: string) => {
          const input = visibleLoanInputs[key]

          return (
            <div className={`input md:col-span-${input.span || 1}`} key={key}>
              <RenderInput Key={key} input={input} onChange={() => {}} />
            </div>
          )
        })}
      </>
    )
  }

  return (
    <div className={`underwriting-summary-container ${!child && 'px-2'}`}>
      {!child && <Overview title={title} />}
      <SaveChanges show={changed} onSave={onSave} />
      <div className="max-w-screen-2xl m-auto">
        <div className={`bg-white rounded ${!child && 'shadow1 mb-6 p-4'}`}>
          <div className="">
            <LayoutLoading show={isLoading} />
            <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
              <span>{title}</span>
              {isLoading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />}
            </h2>
            {!init && (
              <div className="grid grid-cols-1 gap-5">
                {Object.keys(uwInputGroups[loan.transactionType]).map((key, index) => {
                  const inputGroup = uwInputGroups[loan.transactionType][key]

                  return (
                    <div key={index} className="md:col-span-1">
                      <div
                        className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center mb-2`}
                      >
                        {index + 1}. {key}
                        <span className="p-1 hover-shadow1 cursor-pointer rounded" onClick={onDownload}>
                          {index === 0 && <PrinterIcon className="w-5 h-5 text-shade-blue" />}
                        </span>
                      </div>

                      <div className="grid gap-4 md:grid-cols-4 grid-cols-1 mb-3">
                        {inputGroup.map((item: string) => {
                          const sectionKey = Object.values(UwSummarySection).find(
                            (v) => UwSummarySectionText[v] === key,
                          )
                          if (sectionKey && !fieldIsVisible(sectionKey, item)) return
                          if (item === 'ManufacturedHomeSecuredPropertyType' && loan.propertyType !== 'Manufactured')
                            return
                          if (
                            [
                              'ExceptionType',
                              'ExceptionDetail',
                              'ExceptionRequestedDate',
                              'ExceptionApprovedDate',
                              'ExceptionApprover',
                              'ExceptionApproved',
                            ].includes(item) &&
                            !inputs.HasException.value
                          )
                            return
                          if (
                            item === 'BorrowersInterestSourceTitleInSubjectProperty' &&
                            inputs['BorrowersInterestSourceTypeInSubjectProperty'].value !== 'other'
                          )
                            return <div></div>
                          if (!inputs[item]) return null
                          if (item === 'DisclosedLitigation') return renderDisclosedLitigation()
                          if (item === 'DeedRecordingDate') return null
                          let input = cloneDeep(inputs[item])
                          const additionalElements = []
                          const naKey = `${item}NA`

                          if (hasNAFields.includes(item)) {
                            additionalElements.push(
                              <Checkbox
                                id={naKey}
                                title=""
                                className="w-[unset]"
                                value={!!data[naKey]}
                                onChange={(value) => onChangeNA(naKey, value)}
                              />,
                            )
                          }
                          input.additionalElements = <div className="flex items-center gap-2">{additionalElements}</div>

                          if (item === 'ManufacturedHomeLandPropertyInterest') {
                            if (loan.propertyType !== 'Manufactured') {
                              input.value = '5'
                              input.disabled = true
                            } else {
                              input.disabled = false
                            }
                          }

                          if (sectionKey) input.required = config[sectionKey]?.[item]?.required === true

                          if (!!input?.key && !!fieldProperties[input.key]) {
                            if (
                              !!fieldProperties[input.key]?.options &&
                              !!fieldProperties[input.key].options?.length &&
                              [...loanCustomRangeOptionFields, ...loanCustomSelectOptionFields].includes(input.key)
                            ) {
                              ;(input as any).options = convertSelectRangeOptionsToMap(
                                fieldProperties[input.key]?.options || [],
                              )
                            }
                            if (editableOptionFields.includes(input.key) && fieldProperties[input.key]?.options?.length) {
                              const labels = customOptionLabels(fieldProperties[input.key].options as any)
                              if (labels) (input as any).options = labels
                            }
                          }

                          if (item === 'constructionCompanyAddressMap') {
                            return (
                              <>
                                {index == 0 && renderLoanInputs()}
                                <div className={`input md:col-span-${input.span || 2} h-[400px]`} key={item}>
                                  <RenderInput
                                    input={{
                                      ...input,
                                      value: latLong as any,
                                    }}
                                    Key={key}
                                    onChange={onChange}
                                  />
                                </div>
                              </>
                            )
                          }
                          if (item === 'ExceptionType') {
                            input.options = [...exceptionConfig.types]
                          }
                          input.history = !input.disabled
                          input.className = 'flex items-center'

                          if (item === 'AIV2MedianRate')
                            input.value =
                              Math.round(
                                ((Number(loan.asIsValue) * 100) /
                                  Number(removeComma(inputs['MedianHomePrice'].value))) *
                                  100,
                              ) / 100
                          if (item === 'ARV2MedianRate')
                            input.value =
                              Math.round(
                                ((Number(loan.afterRepairValue) * 100) /
                                  Number(removeComma(inputs['MedianHomePrice'].value))) *
                                  100,
                              ) / 100

                          return (
                            <div className={`input md:col-span-${input.span || 1}`} key={item}>
                              <RenderInput input={input} Key={item} onChange={onChange} showHistory={showHistory} />
                            </div>
                          )
                        })}
                      </div>
                      {key === 'Source of Borrowers Interest In Subject Property' && (
                        <div className="p-2">{renderRecordingInformation()}</div>
                      )}
                    </div>
                  )
                })}

                <div className="flex justify-center">
                  <Button className="w-full sm:w-[240px]" loading={isLoading} onClick={onSave}>
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
